import type { BaseEditor, Descendant } from 'slate';
import type { ReactEditor } from 'slate-react';
import type { HistoryEditor } from 'slate-history';

export type ParagraphElement = {
  type: 'paragraph';
  children: CustomText[];
};

export type HeadingElement = {
  type: 'heading-one' | 'heading-two';
  children: CustomText[];
};

export type BlockQuoteElement = {
  type: 'block-quote';
  children: CustomText[];
};

export type CodeBlockElement = {
  type: 'code-block';
  children: CustomText[];
};

export type BulletedListElement = {
  type: 'bulleted-list';
  children: ListItemElement[];
};

export type NumberedListElement = {
  type: 'numbered-list';
  children: ListItemElement[];
};

export type ListItemElement = {
  type: 'list-item';
  children: CustomText[];
};

export type LinkElement = {
  type: 'link';
  url: string;
  children: CustomText[];
};

export type CustomElement =
  | ParagraphElement
  | HeadingElement
  | BlockQuoteElement
  | CodeBlockElement
  | BulletedListElement
  | NumberedListElement
  | ListItemElement
  | LinkElement;

export type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor;

export type MarkType = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'code';
export type BlockType =
  'paragraph' | 'heading-one' | 'heading-two' | 'block-quote' | 'code-block' | 'bulleted-list' | 'numbered-list';

export interface ToolbarPreset {
  marks: MarkType[];
  blocks: BlockType[];
  enableLinks: boolean;
}

export const TOOLBAR_PRESETS: Record<string, ToolbarPreset> = {
  thread: {
    marks: ['bold', 'italic', 'underline', 'strikethrough'],
    blocks: ['bulleted-list', 'numbered-list', 'block-quote'],
    enableLinks: true,
  },
  full: {
    marks: ['bold', 'italic', 'underline', 'strikethrough', 'code'],
    blocks: ['heading-one', 'heading-two', 'bulleted-list', 'numbered-list', 'code-block', 'block-quote'],
    enableLinks: true,
  },
  minimal: {
    marks: ['bold', 'italic'],
    blocks: [],
    enableLinks: false,
  },
  plain: {
    marks: [],
    blocks: [],
    enableLinks: false,
  },
};

export interface RichTextEditorProps {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  preset?: keyof typeof TOOLBAR_PRESETS;
  customPreset?: ToolbarPreset;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}
